<template>
  <div class="segmented-control" :class="{ 'segmented-control-disabled': disabled }">
    <div
      class="segmented-slider"
      :style="sliderStyle"
    ></div>

    <div
      v-for="(item, index) in options"
      :key="getItemKey(item, index)"
      class="segmented-item"
      :class="{
        'segmented-item-active': isActive(item, index),
        'segmented-item-disabled': disabled || item.disabled
      }"
      @click="handleItemClick(item, index)"
    >
      <span class="segmented-item-text">{{ getItemLabel(item) }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SegmentedControl',
  props: {
    // 选项数组，可以是字符串数组或对象数组
    options: {
      type: Array,
      required: true,
      default: () => []
    },
    // 当前选中的值
    segmentedValue: {
      type: [String, Number],
      default: null
    },
    // 是否禁用整个组件
    disabled: {
      type: Boolean,
      default: false
    },
    // 尺寸大小
    size: {
      type: String,
      default: 'medium',
      validator: value => ['small', 'medium', 'large'].includes(value)
    },
    // 激活时的颜色
    activeColor: {
      type: String,
      default: '#ff5e2a'
    },
    // 非激活时的颜色
    inactiveColor: {
      type: String,
      default: '#F5F7FA'
    },
    // 激活时的文字颜色
    activeTextColor: {
      type: String,
      default: '#FFFFFF'
    },
    // 非激活时的文字颜色
    inactiveTextColor: {
      type: String,
      default: '#ababab'
    },
    // 是否在改变前进行确认
    beforeChange: {
      type: Function,
      default: null
    }
  },
  model: {
    prop: 'segmentedValue',
    event: 'change'
  },
  computed: {
    // 计算滑动背景的样式
    sliderStyle() {
      const activeIndex = this.getActiveIndex()
      if (activeIndex === -1) return {}

      const itemWidth = 100 / this.options.length // 每个选项的宽度百分比
      const translateX = activeIndex * 100

      return {
        width: `${itemWidth - 1}%`,
        transform: `translateX(${translateX}%)`,
        backgroundColor: this.activeColor
      }
    }
  },
  methods: {
    // 获取当前激活选项的索引
    getActiveIndex() {
      for (let i = 0; i < this.options.length; i++) {
        const item = this.options[i]
        const itemValue = this.getItemValue(item, i)
        if (this.segmentedValue === itemValue) {
          return i
        }
      }
      return -1
    },
    // 获取选项的key
    getItemKey(item, index) {
      if (typeof item === 'object' && item.value !== undefined) {
        return item.value
      }
      return index
    },

    // 获取选项的显示文本
    getItemLabel(item) {
      if (typeof item === 'string') {
        return item
      }
      if (typeof item === 'object' && item.label !== undefined) {
        return item.label
      }
      return String(item)
    },

    // 获取选项的值
    getItemValue(item, index) {
      if (typeof item === 'object' && item.value !== undefined) {
        return item.value
      }
      return item
    },

    // 判断选项是否激活
    isActive(item, index) {
      const itemValue = this.getItemValue(item, index)
      return this.segmentedValue === itemValue
    },

    // 处理选项点击
    async handleItemClick(item, index) {
      if (this.disabled || (typeof item === 'object' && item.disabled)) {
        return
      }

      const itemValue = this.getItemValue(item, index)

      // 如果点击的是当前激活的选项，不处理
      if (this.segmentedValue === itemValue) {
        return
      }

      // 如果有beforeChange函数，先执行确认
      if (this.beforeChange) {
        try {
          const result = await this.beforeChange(itemValue, item, index)
          if (result === false) {
            return
          }
        } catch (error) {
          console.error('beforeChange error:', error)
          return
        }
      }

      this.$emit('change', itemValue)
    }
  }
}
</script>

<style lang="scss" scoped>
.segmented-control {
  width: 100%;
  display: inline-flex;
  background-color: v-bind(inactiveColor);
  border-radius: 6px;
  padding: 2px;
  border: 1px solid #E4E7ED;
  user-select: none;
  position: relative;

  &.segmented-control-disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

// 滑动背景
.segmented-slider {
  position: absolute;
  top: 2px;
  left: 2px;
  height: calc(100% - 4px);
  border-radius: 4px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.segmented-item {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0px 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  min-width: 0;
  z-index: 2; // 确保文字在滑动背景之上

  &.segmented-item-disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:hover:not(.segmented-item-active):not(.segmented-item-disabled) {
    background-color: rgba(0, 0, 0, 0.05);
  }
}

.segmented-item-text {
  font-size: 14px;
  font-weight: 500;
  color: v-bind(inactiveTextColor);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all 0.3s ease;
  position: relative;
  line-height: 32px;

  .segmented-item-active & {
    color: v-bind(activeTextColor);
    font-weight: 600;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  .segmented-item-disabled & {
    color: #C0CCDA;
  }
}

// 尺寸变体
.segmented-control[data-size="small"] {
  .segmented-item {
    padding: 6px 12px;
  }

  .segmented-item-text {
    font-size: 12px;
  }
}

.segmented-control[data-size="large"] {
  .segmented-item {
    padding: 10px 20px;
  }

  .segmented-item-text {
    font-size: 16px;
  }
}

// 响应式设计
@media (max-width: 768px) {
  .segmented-item {
    padding: 6px 12px;
  }

  .segmented-item-text {
    font-size: 12px;
  }
}

// 动画效果
.segmented-item {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

// 焦点样式
.segmented-item:focus-visible {
  outline: 2px solid v-bind(activeColor);
  outline-offset: 2px;
}
</style>
